import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Group,
  Text,
  Stack,
  Box,
  Avatar,
  Badge,
  Collapse,
  ActionIcon,
} from '@mantine/core';
import {
  ChevronDown,
  ChevronRight,
  Users,
} from 'react-feather';
import type { CoachGroupInfo, CoachSessionInfo } from '../../../services/api/coachPortal';
import type { CoachHuddle } from '../../../types';
import { getGroupAvatarColor } from '../../../utils/groupColors';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { AttendanceRate } from '../../../components/common';
import { SessionRow } from './SessionRow';
import { HuddleRow } from './HuddleRow';
import { SurveyLineItem } from '../../cohorts/components/sessions/SurveyLineItem';
import { GroupBookingLinks } from './GroupBookingLinks';
import { getZoneCode } from '../../../utils/formatters';

/** Format time slot into badge text like "Mondays 9:00 AM ET" */
function formatTimeSlotBadge(timeSlot: { day_of_week: string; time: string; timezone: string }): string {
  const dayMap: Record<string, string> = {
    monday: 'Mondays', tuesday: 'Tuesdays', wednesday: 'Wednesdays',
    thursday: 'Thursdays', friday: 'Fridays', saturday: 'Saturdays', sunday: 'Sundays',
  };
  const dayLabel = dayMap[timeSlot.day_of_week.toLowerCase()] || timeSlot.day_of_week;

  // Format time from "09:00:00" to "9:00 AM"
  const [hours, minutes] = timeSlot.time.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  const timeStr = `${displayHour}:${String(minutes).padStart(2, '0')} ${ampm}`;

  // Timezone abbreviation
  try {
    const tzAbbr = getZoneCode(timeSlot.timezone, new Date());
    return `${dayLabel} ${timeStr} ${tzAbbr}`;
  } catch {
    return `${dayLabel} ${timeStr}`;
  }
}

/** Stat block matching CM card styling */
function GroupStatBlock({ value, label }: { value: string; label: string }) {
  return (
    <Box
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 6,
        padding: '10px 20px',
        textAlign: 'center' as const,
        minWidth: 70,
      }}
    >
      <Text fw={700} size="20px" lh={1}>
        {value}
      </Text>
      <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>
        {label}
      </Text>
    </Box>
  );
}

export interface GroupSessionsCardProps {
  cohortId: string;
  group: CoachGroupInfo;
  expanded: boolean;
  onToggle: () => void;
  onSessionClick: (sessionId: string) => void;
  huddles: CoachHuddle[];
  onHuddleClick: (huddleId: string) => void;
  onEditHuddle: (huddle: CoachHuddle) => void;
  onCancelHuddle: (huddle: CoachHuddle) => void;
  searchQuery?: string;
  onOpenSurveyResults?: (
    groupId: string,
    groupName: string,
    tab?: 'post_session' | 'midpoint' | 'endpoint',
  ) => void;
  onOpenCoachFeedback?: (surveyType: 'midpoint' | 'endpoint') => void;
  isPartnerCoach?: boolean;
  /**
   * MED-698: Controls visibility of General check-ins interleaved in the
   * timeline. MED-1350 moved intro and midpoint check-ins to the 1:1 section.
   * Defaults to true so existing callers keep current behavior until they opt in.
   */
  showOneOnOnes?: boolean;
  /**
   * MED-1181 / AC2: the cohort's group-coaching per-modality Live Session
   * Delivery toggle. ANDed with the global flag to gate the "Prep Session" /
   * "Start Session" button + "PREP AVAILABLE" badge. False for a classic MED01
   * cohort → no prep affordance renders.
   */
  liveSessionDeliveryModalityEnabled: boolean;
  /**
   * MED-1319: shows the "Booking links" button. False when the cohort has no
   * touchpoint that a booking link can book. Defaults to true.
   */
  showBookingLinks?: boolean;
}

export function GroupSessionsCard({
  cohortId,
  group,
  expanded,
  onToggle,
  onSessionClick,
  huddles,
  onHuddleClick,
  onEditHuddle,
  onCancelHuddle,
  searchQuery = '',
  onOpenSurveyResults,
  onOpenCoachFeedback,
  isPartnerCoach = false,
  showOneOnOnes = true,
  liveSessionDeliveryModalityEnabled,
  showBookingLinks = true,
}: GroupSessionsCardProps) {
  const navigate = useNavigate();
  // MED-890 / AC2: Hide the "Prep Session" / "Start Session" affordance on each
  // SessionRow while the live_session_delivery flag is off (or still loading —
  // useFeatureFlag fails closed). When the flag is off we omit prepStatus and
  // onPrepClick entirely so SessionRow renders no prep badge or button.
  // MED-1181 / AC2: also AND-in the cohort's per-modality LSD toggle so the
  // affordance stays hidden for a classic MED01 cohort even when the global
  // flag is on.
  const liveSessionDeliveryEnabled =
    useFeatureFlag('live_session_delivery') && liveSessionDeliveryModalityEnabled;
  const completedSessions = group.sessions.filter((s) => s.status === 'completed').length;
  const totalSessions = group.sessions.length;

  // MED-698 E6-10b: General huddles interleave chronologically with sessions.
  // MED-1350: intro and midpoint check-ins are 1:1 rows now and show in the
  // 1:1 section, not in the group card.
  const activeAll = huddles.filter((h) => h.status !== 'cancelled');
  const generalHuddles = showOneOnOnes
    ? activeAll
        .filter((h) => h.huddle_type === 'general' && !!h.scheduled_start)
        .sort(
          (a, b) =>
            new Date(a.scheduled_start).getTime() - new Date(b.scheduled_start).getTime(),
        )
    : [];

  // MED-1266: POOLED group attendance — sum canonical per-session numerators (attended +
  // partial) and denominators (marked) across completed sessions, then divide once. The
  // prior implementation averaged each session's percentage (a mean-of-means), which
  // violates the canonical pooled rule; this consumes the server's per-session figures
  // rather than inventing a rate. Coverage (marked of population) is pooled the same way.
  const sessionsWithAttendance = group.sessions.filter(
    (s) => s.status === 'completed' && s.attendance_summary,
  );
  const pooled = sessionsWithAttendance.reduce(
    (acc, s) => {
      const a = s.attendance_summary!;
      acc.numerator += a.attended + a.partial;
      acc.marked += a.marked;
      acc.population += a.population;
      return acc;
    },
    { numerator: 0, marked: 0, population: 0 },
  );
  const avgAttendance =
    pooled.marked > 0 ? Math.round((pooled.numerator / pooled.marked) * 100) : null;

  // Average engagement across sessions with data
  const sessionsWithEngagement = group.sessions.filter(s => s.avg_engagement != null);
  const avgEngagement = sessionsWithEngagement.length > 0
    ? Math.round(
        sessionsWithEngagement.reduce((sum, s) => sum + (s.avg_engagement || 0), 0)
        / sessionsWithEngagement.length * 10
      ) / 10
    : null;

  // Build unified chronological timeline (sessions + huddles)
  type TimelineItem =
    | { type: 'session'; data: CoachSessionInfo; index: number }
    | { type: 'huddle'; data: CoachHuddle };

  const allTimelineItems: TimelineItem[] = [
    ...group.sessions.map((s, i) => ({ type: 'session' as const, data: s, index: i })),
    ...generalHuddles.map((h) => ({ type: 'huddle' as const, data: h, index: -1 })),
  ].sort((a, b) => {
    const dateA = new Date(a.data.scheduled_start).getTime();
    const dateB = new Date(b.data.scheduled_start).getTime();
    return dateA - dateB;
  });

  // Apply search filter
  const query = searchQuery.toLowerCase().trim();
  const timeline = query
    ? allTimelineItems.filter((item) => {
        if (item.type === 'session') {
          const s = item.data;
          return (
            (s.title && s.title.toLowerCase().includes(query)) ||
            (s.topic_name && s.topic_name.toLowerCase().includes(query))
          );
        }
        return true;
      })
    : allTimelineItems;

  // Midpoint index for survey placement
  const midpointIndex = Math.floor((totalSessions - 1) / 2);

  const groupAvatarColor = getGroupAvatarColor(group.name, group.id);
  const timeSlotBadge = group.time_slot ? formatTimeSlotBadge(group.time_slot) : null;

  const handleReviewSurvey = (tab: 'midpoint' | 'endpoint') => {
    onOpenSurveyResults?.(group.id, group.name, tab);
  };

  return (
    <Box
      mb="md"
      style={{
        border: '1px solid #E5E2DC',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Group header - clickable for accordion */}
      <Group
        justify="space-between"
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          backgroundColor: '#FFFFFF',
          padding: '16px 20px',
          borderBottom: expanded ? '1px solid #E5E2DC' : 'none',
        }}
        wrap="nowrap"
        className="med-mobile-wrap"
      >
        <Group gap="sm" wrap="nowrap" className="med-mobile-wrap">
          <Avatar
            size={40}
            radius="xl"
            styles={{
              root: { backgroundColor: groupAvatarColor },
              placeholder: { color: '#FFFFFF', backgroundColor: groupAvatarColor },
            }}
          >
            <Users size={20} color="white" />
          </Avatar>
          <div>
            <Text fw={600} size="sm">
              {group.name}
            </Text>
            <Text size="13px" c="dimmed">
              {group.participant_count} participant{group.participant_count !== 1 ? 's' : ''}
            </Text>
          </div>
          {timeSlotBadge && (
            <Badge
              variant="light"
              color="gray"
              size="lg"
              radius="xl"
              styles={{ label: { fontWeight: 500, fontSize: 13, textTransform: 'none', letterSpacing: 0 } }}
            >
              {timeSlotBadge}
            </Badge>
          )}
          {/* MED-1319: share links for logged-out 1:1 booking */}
          {showBookingLinks && (
            <GroupBookingLinks groupId={group.id} groupName={group.name} />
          )}
        </Group>

        <Group gap="sm" wrap="nowrap" className="med-mobile-stats">
          <GroupStatBlock
            value={`${completedSessions}/${totalSessions}`}
            label="Sessions"
          />
          {/* MED-1266: pooled rate + marking coverage via the shared component. */}
          <Box
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 6,
              padding: '10px 20px',
              textAlign: 'center' as const,
              minWidth: 70,
            }}
          >
            <AttendanceRate
              rate={avgAttendance}
              marked={pooled.marked}
              population={pooled.population}
              variant="stacked"
              size="20px"
              fw={700}
            />
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>
              Attendance
            </Text>
          </Box>
          <GroupStatBlock
            value={avgEngagement !== null ? avgEngagement.toFixed(1) : '—'}
            label="Avg Engagement"
          />
          <ActionIcon variant="subtle" color="gray" size="sm">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </ActionIcon>
        </Group>
      </Group>

      {/* Collapsible unified timeline */}
      <Collapse in={expanded}>
        {timeline.length > 0 ? (
          <Stack gap={0}>
            {timeline.map((item, idx) => {
              if (item.type === 'huddle') {
                return (
                  <HuddleRow
                    key={`huddle-${item.data.id}`}
                    huddle={item.data}
                    onClick={() => onHuddleClick(item.data.id)}
                    onEdit={() => onEditHuddle(item.data)}
                    onCancel={() => onCancelHuddle(item.data)}
                  />
                );
              }

              const session = item.data;
              const sessionIndex = item.index;

              // MED-793 / AC3 — prep status (client-side computation).
              const priorSession = sessionIndex > 0 ? group.sessions[sessionIndex - 1] : null;
              const startMs = session.scheduled_start
                ? new Date(session.scheduled_start).getTime()
                : null;
              const isLive = session.status === 'in_progress';
              const isPastOrDone = session.status === 'completed' || session.status === 'cancelled';
              const isUnlocked =
                sessionIndex === 0 || priorSession?.status === 'completed';
              const isTMinus15 =
                isUnlocked && startMs !== null && Date.now() >= startMs - 15 * 60 * 1000;
              const prepStatus: 'locked' | 'available' | 't_minus_15' | 'live' | 'past' = isLive
                ? 'live'
                : isPastOrDone
                  ? 'past'
                  : !isUnlocked
                    ? 'locked'
                    : isTMinus15
                      ? 't_minus_15'
                      : 'available';

              return (
                <Fragment key={`session-${session.id}-${idx}`}>
                  <SessionRow
                    session={session}
                    groupName={group.name}
                    onClick={() => onSessionClick(session.id)}
                    prepStatus={liveSessionDeliveryEnabled ? prepStatus : undefined}
                    onPrepClick={
                      liveSessionDeliveryEnabled
                        ? () =>
                            navigate(
                              `/coach/cohorts/${cohortId}/sessions/${session.id}/prep`,
                            )
                        : undefined
                    }
                  />
                  {!isPartnerCoach && sessionIndex === midpointIndex && (
                    <SurveyLineItem
                      cohortId={cohortId}
                      groupId={group.id}
                      surveyType="midpoint"
                      colSpan={0}
                      onReview={() => handleReviewSurvey('midpoint')}
                      onReviewCoaches={onOpenCoachFeedback ? () => onOpenCoachFeedback('midpoint') : undefined}
                      coachButtonLabel="My Feedback"
                    />
                  )}
                  {!isPartnerCoach && sessionIndex === totalSessions - 1 && (
                    <SurveyLineItem
                      cohortId={cohortId}
                      groupId={group.id}
                      surveyType="endpoint"
                      colSpan={0}
                      onReview={() => handleReviewSurvey('endpoint')}
                      onReviewCoaches={onOpenCoachFeedback ? () => onOpenCoachFeedback('endpoint') : undefined}
                      coachButtonLabel="My Feedback"
                    />
                  )}
                </Fragment>
              );
            })}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {query ? 'No sessions match your search' : 'No sessions scheduled yet'}
          </Text>
        )}
      </Collapse>
    </Box>
  );
}
